import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  updatePassword,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  setDoc,
  addDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_MEASUREMENT_ID
};

// Validate configuration
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error('Firebase configuration is missing. Please check your .env file.');
  throw new Error('Firebase configuration is incomplete');
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);

// Firebase service object
export const firebaseService = {
  // Auth methods
  login: async (email, password) => {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  onAuthStateChange: (callback) => {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Get additional user data from Firestore if needed
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const profileData = { ...userDoc.data() };
            // Ensure auth-managed fields like emailVerified are NOT overwritten by firestore data
            delete profileData.emailVerified;
            Object.assign(user, profileData);
          }
          callback(user);
        } catch (error) {
          console.error('Error fetching user data:', error);
          callback(user);
        }
      } else {
        callback(null);
      }
    });
  },

  signup: async (email, password, userData) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Create user profile in Firestore with provided data
      const userDoc = doc(db, 'users', userCredential.user.uid);
      await setDoc(userDoc, {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        ...userData,
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp()
      });

      return userCredential;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  },

  logout: async () => {
    try {
      await signOut(auth);
      return true;
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  // Google Sign-In
  googleSignIn: async () => {
    try {
      const provider = new GoogleAuthProvider();
      await setPersistence(auth, browserLocalPersistence);
      const result = await signInWithPopup(auth, provider);

      // Create or update user in Firestore
      const userDoc = doc(db, 'users', result.user.uid);
      await setDoc(userDoc, {
        uid: result.user.uid,
        email: result.user.email,
        displayName: result.user.displayName,
        photoURL: result.user.photoURL,
        provider: 'google.com',
        lastLogin: serverTimestamp()
      }, { merge: true });

      return result; // Return full credential object
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  },

  // Get user data from Firestore
  getUserData: async (userId) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return null;
    } catch (error) {
      console.error('Error fetching user data:', error);
      throw error;
    }
  },

  // Password reset via custom backend (with verveai.co action link) with fallback to Firebase Auth
  resetPassword: async (email) => {
    const cleanEmail = (email || '').trim().toLowerCase();
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://verveai.co';
    try {
      try {
        const resp = await fetch('https://admin.verveailabs.com/api/auth/send-custom-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'PASSWORD_RESET',
            email: cleanEmail,
            redirectUrl: `${currentOrigin}/login`,
          })
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.success) {
            return { success: true };
          }
        }
      } catch (backendErr) {
        console.warn('Backend custom password reset error, falling back to Firebase SDK:', backendErr);
      }

      const actionCodeSettings = {
        url: `${currentOrigin}/login`,
        handleCodeInApp: false
      };
      await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  },

  // Update user profile
  updateUserProfile: async (userId, data) => {
    try {
      const userDoc = doc(db, 'users', userId);
      await setDoc(userDoc, {
        ...data,
        updatedAt: serverTimestamp()
      }, { merge: true });
      return true;
    } catch (error) {
      console.error('Update profile error:', error);
      throw error;
    }
  },

  // Update user role
  updateUserRole: async (userId, newRole) => {
    try {
      const userDoc = doc(db, 'users', userId);
      await updateDoc(userDoc, {
        role: newRole,
        updatedAt: serverTimestamp()
      });
      return true;
    } catch (error) {
      console.error('Update role error:', error);
      throw error;
    }
  },

  // Update password
  updateUserPassword: async (password) => {
    try {
      const user = auth.currentUser;
      if (user) {
        await updatePassword(user, password);
        return true;
      }
      throw new Error('No user logged in');
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  }
};

// Export Firebase instances
export { functions, db, auth };

/**
 * Normalizes user-entered phone number into valid international E.164 format.
 * Defaults to India (+91) if 10 digits are provided without a country prefix.
 */
export const normalizePhoneNumber = (rawPhone) => {
  if (!rawPhone) return '';
  const cleaned = rawPhone.trim().replace(/[\s\-()]/g, '');
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  const digitsOnly = cleaned.replace(/^0+/, '');
  if (digitsOnly.length === 10) {
    return `+91${digitsOnly}`;
  }
  return `+${digitsOnly}`;
};

/**
 * Initializes invisible reCAPTCHA verifier for Firebase Phone Authentication
 */
export const setupPhoneRecaptcha = (containerId) => {
  if (typeof window === 'undefined') return null;

  if (window.phoneRecaptchaVerifier) {
    try {
      window.phoneRecaptchaVerifier.clear();
    } catch {}
    window.phoneRecaptchaVerifier = null;
  }

  const el = document.getElementById(containerId);
  if (el) {
    el.innerHTML = '';
  }

  const verifier = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => {
      if (window.phoneRecaptchaVerifier) {
        try { window.phoneRecaptchaVerifier.clear(); } catch {}
        window.phoneRecaptchaVerifier = null;
      }
    },
  });

  window.phoneRecaptchaVerifier = verifier;
  return verifier;
};

/**
 * Sends a real SMS OTP verification code to the customer's phone number
 */
export const sendPhoneOtpSms = async (phoneNumber, appVerifier) => {
  try {
    await appVerifier.render();
  } catch (e) {
    console.warn('reCAPTCHA render notice:', e);
  }
  return await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
};

/**
 * Maps Firebase Phone Auth error codes to user-friendly messages
 */
export const mapPhoneAuthError = (err) => {
  const code = err?.code || '';
  switch (code) {
    case 'auth/invalid-phone-number':
      return 'Invalid mobile number format. Please ensure correct country code (e.g. +91 93733 38696).';
    case 'auth/missing-phone-number':
      return 'Please enter a valid mobile number.';
    case 'auth/quota-exceeded':
      return 'SMS verification quota reached. Please connect with our team directly via WhatsApp.';
    case 'auth/too-many-requests':
      return 'Too many SMS requests. Please wait a minute before requesting another code.';
    case 'auth/captcha-check-failed':
      return 'Security verification check failed. Please refresh the page and try again.';
    case 'auth/invalid-verification-code':
      return 'Incorrect 6-digit code. Please enter the exact code received via SMS on your mobile.';
    case 'auth/code-expired':
      return 'Verification code has expired. Please click Resend SMS to get a new code.';
    default:
      return err?.message || 'Verification failed. Please check the code and try again.';
  }
};

export default firebaseService;
